// src/pages/Tour/DailyVisits.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dealerVisitService from '../../services/dealerVisit.service';
import farmerVisitService from '../../services/farmerVisit.service';
import Toast from '../../components/Toast/Toast';
import './TourPages.css';

const formatTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const formatDateHeading = (dateStr) => {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-IN', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });
};

const DailyVisits = () => {
  const navigate = useNavigate();
  const { date } = useParams(); // YYYY-MM-DD

  const [dealerVisits, setDealerVisits] = useState([]);
  const [farmerVisits, setFarmerVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);
  const [tab, setTab]         = useState('DEALER'); // 'DEALER' | 'FARMER'

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [dealerRes, farmerRes] = await Promise.allSettled([
          dealerVisitService.getAllVisits(date, date, '', 1, 100),
          farmerVisitService.getVisitsByDate(date),
        ]);

        if (dealerRes.status === 'fulfilled' && dealerRes.value.success) {
          setDealerVisits(dealerRes.value.data?.visits || dealerRes.value.data || []);
        }
        if (farmerRes.status === 'fulfilled' && farmerRes.value.success) {
          setFarmerVisits(farmerRes.value.data || []);
        }
      } catch {
        setToast({ message: 'Failed to load visits', type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [date]);

  return (
    <div className="tour-page-container" style={{ maxWidth: 900 }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav className="tour-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a><span>›</span>
        <button className="tour-breadcrumb-btn" onClick={() => navigate('/tour/monthly')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0 }}>
          Tour
        </button>
        <span>›</span><span>Visits</span>
      </nav>

      <div className="tour-header">
        <h4 className="tour-title">Visits — {formatDateHeading(date)}</h4>
        <p className="tour-subtitle">Farmer and dealer visits logged on this day</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => setTab('DEALER')}
          style={{
            flex: 1, padding: '10px 16px', borderRadius: 8,
            border: tab === 'DEALER' ? '2px solid #4a8c2a' : '1px solid #e0e0e0',
            background: tab === 'DEALER' ? '#e8f5e9' : '#fff',
            color: tab === 'DEALER' ? '#2e7d32' : '#495057',
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          🏪 Dealer / Sub-Dealer Visits ({dealerVisits.length})
        </button>
        <button
          onClick={() => setTab('FARMER')}
          style={{
            flex: 1, padding: '10px 16px', borderRadius: 8,
            border: tab === 'FARMER' ? '2px solid #4a8c2a' : '1px solid #e0e0e0',
            background: tab === 'FARMER' ? '#e8f5e9' : '#fff',
            color: tab === 'FARMER' ? '#2e7d32' : '#495057',
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          🌾 Farmer Visits ({farmerVisits.length})
        </button>
      </div>

      <div className="tour-table-container">
        {loading ? (
          <div className="tour-loading">Loading visits...</div>
        ) : tab === 'DEALER' ? (
          <table className="tour-table">
            <thead>
              <tr><th>#</th><th>Type</th><th>Name</th><th>Time</th><th>Location</th><th>Remark</th></tr>
            </thead>
            <tbody>
              {dealerVisits.length > 0 ? dealerVisits.map((visit, i) => (
                <tr key={visit.id}>
                  <td style={{ fontWeight: 600, color: '#4a8c2a' }}>{i + 1}</td>
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      background: visit.subDealerId ? '#fff3e0' : '#e8f5e9',
                      color: visit.subDealerId ? '#e65100' : '#2e7d32',
                    }}>
                      {visit.subDealerId ? 'SUB-DEALER' : 'DEALER'}
                    </span>
                  </td>
                  <td style={{ fontWeight: 500 }}>
                    {visit.subDealer?.subDealerShopName || visit.dealer?.dealerShopName || '—'}
                    {visit.subDealerId && (
                      <div style={{ fontSize: 11, color: '#6c757d' }}>Under: {visit.dealer?.dealerShopName}</div>
                    )}
                  </td>
                  <td>{formatTime(visit.visitTime)}</td>
                  <td>
                    {visit.visitLatitude
                      ? <span style={{ fontSize: 12, color: '#4a8c2a' }}>
                          📍 {parseFloat(visit.visitLatitude).toFixed(4)}, {parseFloat(visit.visitLongitude).toFixed(4)}
                        </span>
                      : <span className="km-pending">—</span>}
                  </td>
                  <td style={{ maxWidth: 200, fontSize: 13, color: '#495057' }}>
                    {visit.remark || <span className="km-pending">—</span>}
                  </td>
                </tr>
              )) : (
                <tr className="no-data-row"><td colSpan="6">No dealer visits logged on this day</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="tour-table">
            <thead>
              <tr><th>#</th><th>Type</th><th>Farmer</th><th>Village</th><th>Time</th><th>Crop</th><th>Reason / Remark</th></tr>
            </thead>
            <tbody>
              {farmerVisits.length > 0 ? farmerVisits.map((visit, i) => (
                <tr key={visit.id}>
                  <td style={{ fontWeight: 600, color: '#4a8c2a' }}>{i + 1}</td>
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      background: visit.visitType === 'FIRST_VISIT' ? '#e3f2fd' : '#fff3e0',
                      color: visit.visitType === 'FIRST_VISIT' ? '#1565c0' : '#e65100',
                    }}>
                      {visit.visitType === 'FIRST_VISIT' ? 'FIRST VISIT' : 'FOLLOWUP'}
                    </span>
                  </td>
                  <td style={{ fontWeight: 500 }}>{visit.farmer?.farmerName || '—'}</td>
                  <td>{visit.farmer?.villageRef?.name || visit.farmer?.village || '—'}</td>
                  <td>{formatTime(visit.visitTime)}</td>
                  <td>{visit.crop || <span className="km-pending">—</span>}</td>
                  <td style={{ maxWidth: 220, fontSize: 13, color: '#495057' }}>
                    {visit.reason || visit.remark || <span className="km-pending">—</span>}
                  </td>
                </tr>
              )) : (
                <tr className="no-data-row"><td colSpan="7">No farmer visits logged on this day</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DailyVisits;